import { Dispatch, SetStateAction, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import UsersApi from "../../../api/usersApi";
import { ReactComponent as ChevronLeftIcon } from "../../../assets/Specialists/chevronLeft.svg";
import CircleAvatar from "../../../components/CircleAvatar/CircleAvatar";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import TripleDotPoperMenu from "../../../components/TripleDotPoperMenu/TripleDotPoperMenu";
import { ContextProvider } from "../../../contextProvider";
import { TypeUserData } from "../../../types/TypeUsers";
import { getTranslation } from "../../../utils/getTranslation";
import { formatDate } from "../../../utils/setupProfileInfo";
import s from "./UserInfo.module.css";

type UserInfoProps = {
  selectedUser: TypeUserData;
  setSelectedUser: Dispatch<SetStateAction<TypeUserData | undefined>>;
  avatars: {
    [userId: string]: string;
  };
  setAllUsersData: Dispatch<SetStateAction<TypeUserData[] | undefined>>;
};

const UserInfo = ({
  selectedUser,
  setSelectedUser,
  avatars,
  setAllUsersData,
}: UserInfoProps) => {
  const { setUserIdForCreate } = useContext(ContextProvider);
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [banConfirmModal, setBanConfirmModal] = useState(false);
  const [loaders, setLoaders] = useState({ ban: false });

  const menuItems = [
    {
      title: "Send message",
      action: () => {
        setUserIdForCreate(selectedUser._id);
        navigate("/chat");
      },
    },
    {
      title: selectedUser.isBanned ? "Unban" : "Ban",
      action: () => setBanConfirmModal(true),
      styles: { color: selectedUser.isBanned ? "green" : "red" },
    },
  ];

  const changeUserStatus = async (isBanned: boolean) => {
    if (!token) return;
    setLoaders((prev) => ({ ...prev, ban: true }));
    const response = await UsersApi.updateUserData(token, {
      _id: selectedUser._id,
      isBanned: isBanned,
    });

    setLoaders((prev) => ({ ...prev, ban: false }));
    if (response.status) {
      setSelectedUser((prev) =>
        prev ? { ...prev, isBanned: isBanned } : prev
      );
      setAllUsersData((prev) =>
        prev
          ? prev.map((item) => {
              if (item._id === selectedUser._id) {
                return { ...item, isBanned: isBanned };
              }
              return item;
            })
          : prev
      );
      setBanConfirmModal(false);
    }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div
          className={s.backBlock}
          role="button"
          onClick={() => setSelectedUser(undefined)}
        >
          <ChevronLeftIcon className={s.chevronBackIcon} />
          Back
        </div>

        <TripleDotPoperMenu menuItemDta={menuItems} />
      </div>
      <div className={s.headNameBlock}>
        <CircleAvatar
          /*  userId={selectedChat.members._id} */
          name={getTranslation(selectedUser?.name)}
          surname={getTranslation(selectedUser?.surname)}
          avatarProps={avatars[selectedUser._id]}
        />

        <div className={s.nameEmailBlock}>
          <span>
            {getTranslation(selectedUser?.name) +
              " " +
              getTranslation(selectedUser?.surname)}
          </span>
          <span>{selectedUser?.email}</span>
        </div>
      </div>
      {selectedUser && (
        <div className={s.mainInfoBlock}>
          <div className={s.infoTitleBlock}>
            <span>Personal info</span>
          </div>
          <div className={s.personInfoBlock}>
            <div className={s.personInfoLineBlock}>
              <div className={s.personInfoElement}>
                <span className={s.infoLable}>Name</span>

                <span className={s.infoValue}>
                  {getTranslation(selectedUser.name)}
                </span>
              </div>
              <div className={s.personInfoElement}>
                <span className={s.infoLable}>Surname</span>

                <span className={s.infoValue}>
                  {getTranslation(selectedUser.surname)}
                </span>
              </div>
            </div>
          </div>
          <div className={s.personInfoBlock}>
            <div className={s.personInfoLineBlock}>
              <div className={s.personInfoElement}>
                <span className={s.infoLable}>Date of birth</span>

                <span className={s.infoValue}>
                  {formatDate(
                    selectedUser.dateOfBirth
                      ? new Date(selectedUser.dateOfBirth)
                      : new Date()
                  )}
                </span>
              </div>
              <div className={s.personInfoElement}>
                <span className={s.infoLable}>Time zone</span>

                <span className={s.infoValue}>{selectedUser.timezone}</span>
              </div>
            </div>
          </div>
          <div className={s.personInfoBlock}>
            <div className={s.personInfoLineBlock}>
              <div className={s.personInfoElement}>
                <span className={s.infoLable}>Place of residence</span>

                <span className={s.infoValue}>{selectedUser.location}</span>
              </div>
              <div className={s.personInfoElement}>
                <span className={s.infoLable}>Phone number</span>

                <span className={s.infoValue}>{selectedUser.phoneNumber}</span>
              </div>
            </div>
          </div>
        </div>
      )}
      <ConfirmationModal
        confirmFunc={() => changeUserStatus(!selectedUser.isBanned)}
        modalVisible={banConfirmModal}
        setModalVisible={setBanConfirmModal}
        confirmTitle={`Are you sure you want to ${
          selectedUser.isBanned ? "unban" : "ban"
        } this user?`}
        loader={loaders.ban}
      />
    </div>
  );
};

export default UserInfo;
